import { useState, useEffect, useRef } from "react";
import { api, money } from "@/lib/api";
import { PageHeader, Btn, Field, Select, Badge, Modal } from "@/components/UI";
import { UploadSimple, Sparkle, CheckCircle, Warning, Robot, FileArchive } from "@phosphor-icons/react";

const TYPE_LABEL = {
  visura: "Visura Camerale", investimenti_excel: "Excel Investimenti",
  contratto: "Contratto", preventivo: "Preventivo", oda: "OdA",
  ddt: "DDT", offerta: "Offerta", lettera_incarico: "Lettera incarico",
  fattura: "Fattura", bonifico: "Bonifico", altro: "Altro",
};
const TYPE_TONE = {
  contratto: "blue", preventivo: "blue", oda: "blue",
  fattura: "green", bonifico: "amber",
  visura: "slate", investimenti_excel: "slate", altro: "slate",
};

export default function BatchUpload() {
  const [filiere, setFiliere] = useState([]);
  const [beneficiari, setBeneficiari] = useState([]);
  const [investimenti, setInvestimenti] = useState([]);
  const [fatture, setFatture] = useState([]);
  const [filieraId, setFilieraId] = useState("");
  const [beneficiarioId, setBeneficiarioId] = useState("");
  const [model, setModel] = useState("gemini");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [bundle, setBundle] = useState(null);
  const inputRef = useRef();

  useEffect(() => {
    api.get("/filiere").then((r) => setFiliere(r.data));
    api.get("/beneficiari").then((r) => setBeneficiari(r.data));
    api.get("/investimenti").then((r) => setInvestimenti(r.data));
    api.get("/documenti").then((r) => setFatture(r.data.filter((d) => d.tipo === "fattura")));
  }, []);

  const analyze = async () => {
    if (!filieraId || files.length === 0) { alert("Seleziona filiera e almeno un file"); return; }
    setBusy(true);
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    fd.append("filiera_id", filieraId);
    if (beneficiarioId) fd.append("beneficiario_id", beneficiarioId);
    fd.append("model", model);
    try {
      const { data } = await api.post("/ai/batch-analyze", fd);
      // Add UI state
      const items = data.items.map((it) => ({
        ...it, skip: !!it.error,
        beneficiario_id: it.link_suggestions?.beneficiario_id || beneficiarioId || "",
        investimento_id: it.link_suggestions?.candidates?.[0]?.target_type === "investimento" ? it.link_suggestions.candidates[0].target_id : "",
        parent_doc_id: ["fattura", "bonifico"].includes(it.doc_type) && it.link_suggestions?.candidates?.[0] ? it.link_suggestions.candidates[0].target_id : "",
      }));
      setBundle({ ...data, items });
    } catch (e) { alert("Errore: " + (e.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  };

  const applyBatch = async () => {
    if (!bundle) return;
    setBusy(true);
    try {
      const { data } = await api.post("/ai/apply-batch", {
        filiera_id: filieraId,
        items: bundle.items,
      });
      alert(`Creati: ${data.documenti} documenti, ${data.fornitori} fornitori, ${data.bonifici} bonifici`);
      setBundle(null);
      setFiles([]);
    } catch (e) { alert("Errore: " + (e.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  };

  const updateItem = (idx, patch) => {
    setBundle({ ...bundle, items: bundle.items.map((it, i) => i === idx ? { ...it, ...patch } : it) });
  };

  const filiera_beneficiari = beneficiari.filter((b) => !filieraId || b.filiera_id === filieraId);

  return (
    <div className="fade-in" data-testid="batch-page">
      <PageHeader eyebrow="Document Center" title="Batch Upload — Import massivo AI" />
      <div className="p-8 space-y-6">
        {!bundle ? (
          <>
            <div className="bg-white border border-slate-200 rounded-md p-5">
              <div className="grid grid-cols-3 gap-4">
                <Field label="Filiera *">
                  <Select value={filieraId} onChange={(e) => setFilieraId(e.target.value)} data-testid="batch-filiera">
                    <option value="">--</option>
                    {filiere.map((f) => <option key={f.id} value={f.id}>{f.nome}</option>)}
                  </Select>
                </Field>
                <Field label="Beneficiario (opzionale - AI inferisce)">
                  <Select value={beneficiarioId} onChange={(e) => setBeneficiarioId(e.target.value)} data-testid="batch-beneficiario">
                    <option value="">-- Auto (l'AI infierisce dal documento) --</option>
                    {filiera_beneficiari.map((b) => <option key={b.id} value={b.id}>{b.ragione_sociale}</option>)}
                  </Select>
                </Field>
                <Field label="Modello AI">
                  <Select value={model} onChange={(e) => setModel(e.target.value)} data-testid="batch-model">
                    <option value="gemini">Gemini 3 Flash</option>
                    <option value="openai">GPT-5.4</option>
                    <option value="anthropic">Claude Sonnet 4.5</option>
                  </Select>
                </Field>
              </div>
            </div>

            <div className="border-2 border-dashed border-slate-300 rounded-md p-12 text-center bg-white cursor-pointer"
              onClick={() => inputRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); setFiles([...files, ...Array.from(e.dataTransfer.files)]); }}
              data-testid="batch-dropzone">
              <FileArchive size={40} className="mx-auto text-slate-400" weight="duotone" />
              <div className="mt-2 font-medium">Carica ZIP o multi-file (contratti, fatture, bonifici, visure, Excel investimenti)</div>
              <div className="text-xs text-slate-500 mt-1">L'AI classifica ciascun documento, estrae i dati e propone i collegamenti nella catena</div>
              <input ref={inputRef} type="file" multiple hidden onChange={(e) => setFiles([...files, ...Array.from(e.target.files)])} data-testid="batch-input" />
            </div>

            {files.length > 0 && (
              <div className="bg-white border border-slate-200 rounded-md p-4">
                <div className="eyebrow mb-2">{files.length} file selezionati</div>
                <div className="space-y-1">
                  {files.map((f, i) => (
                    <div key={i} className="flex items-center justify-between text-sm py-1 border-b border-slate-100">
                      <span>{f.name}</span>
                      <span className="text-slate-500 text-xs">{(f.size / 1024).toFixed(1)} KB</span>
                    </div>
                  ))}
                </div>
                <div className="mt-4 flex justify-end gap-2">
                  <Btn variant="secondary" onClick={() => setFiles([])}>Svuota</Btn>
                  <Btn variant="primary" onClick={analyze} disabled={busy || !filieraId} data-testid="batch-analyze-btn">
                    <Robot size={16} weight="duotone" /> {busy ? "Analisi AI in corso…" : `Analizza ${files.length} file`}
                  </Btn>
                </div>
              </div>
            )}
          </>
        ) : (
          <>
            <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-md flex items-center gap-3">
              <Sparkle size={20} weight="fill" className="text-emerald-700" />
              <div className="flex-1">
                <div className="font-medium text-emerald-900">Analisi completata: {bundle.items.length} documenti classificati.</div>
                <div className="text-sm text-emerald-700">Verifica classificazione, collegamenti e deseleziona quelli da escludere. Poi clicca "Applica batch".</div>
              </div>
            </div>

            <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
              <table className="data-table">
                <thead>
                  <tr>
                    <th></th><th>File</th><th>Tipo</th><th>Confidenza</th>
                    <th>Fornitore</th><th>Totale</th>
                    <th>Beneficiario</th><th>Collegamento</th>
                  </tr>
                </thead>
                <tbody>
                  {bundle.items.map((it, i) => {
                    const ex = it.extracted || {};
                    const topSug = it.link_suggestions?.candidates?.[0];
                    return (
                      <tr key={i} className={it.skip ? "opacity-40" : ""} data-testid={`batch-item-${i}`}>
                        <td><input type="checkbox" checked={!it.skip} onChange={(e) => updateItem(i, { skip: !e.target.checked })} data-testid={`batch-sel-${i}`} /></td>
                        <td className="max-w-xs truncate">
                          <div className="font-medium">{it.filename}</div>
                          {it.error && <div className="text-red-600 text-xs mt-0.5">{it.error}</div>}
                        </td>
                        <td>
                          <Select value={it.doc_type} onChange={(e) => updateItem(i, { doc_type: e.target.value })} className="!h-8 !text-xs" data-testid={`batch-type-${i}`}>
                            {Object.entries(TYPE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                          </Select>
                        </td>
                        <td><Badge tone={it.confidence >= 70 ? "green" : it.confidence >= 40 ? "amber" : "red"}>{it.confidence}%</Badge></td>
                        <td className="text-xs">{ex.fornitore_ragione_sociale || "-"}</td>
                        <td className="text-xs">{ex.totale ? money(ex.totale) : (ex.importo ? money(ex.importo) : "-")}</td>
                        <td>
                          <Select value={it.beneficiario_id} onChange={(e) => updateItem(i, { beneficiario_id: e.target.value })} className="!h-8 !text-xs" data-testid={`batch-ben-${i}`}>
                            <option value="">-- Auto --</option>
                            {filiera_beneficiari.map((b) => <option key={b.id} value={b.id}>{b.ragione_sociale}</option>)}
                          </Select>
                        </td>
                        <td>
                          {topSug ? (
                            <div className="text-xs">
                              <div className="flex items-center gap-1">
                                <Badge tone={topSug.confidence >= 70 ? "green" : "amber"}>{topSug.confidence}%</Badge>
                                <span className="truncate max-w-[240px]" title={topSug.label}>{topSug.label}</span>
                              </div>
                            </div>
                          ) : it.doc_type !== "altro" && it.doc_type !== "visura" && it.doc_type !== "investimenti_excel" ? (
                            <span className="text-amber-600 text-xs flex items-center gap-1"><Warning size={12} />Nessun link</span>
                          ) : <span className="text-slate-400 text-xs">-</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="flex justify-between">
              <Btn variant="secondary" onClick={() => setBundle(null)}>← Annulla e ricarica</Btn>
              <Btn variant="primary" onClick={applyBatch} disabled={busy} data-testid="batch-apply-btn">
                <CheckCircle size={16} weight="bold" /> {busy ? "Applicazione…" : `Applica batch (${bundle.items.filter((it) => !it.skip).length} elementi)`}
              </Btn>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
